/**
 * Company detail page with tabs
 */

import { useState, lazy, Suspense } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useCompanyOverview } from '../hooks/useCompanies';
import { useAuth } from '../contexts/AuthContext';
import { KBStatusBadge } from '../components/company/KBStatusBadge';
import { OverviewTab } from '../components/company/tabs/OverviewTab';
import { NewsTab } from '../components/company/tabs/NewsTab';
import { ProductsTab } from '../components/company/tabs/ProductsTab';
import { CustomersTab } from '../components/company/tabs/CustomersTab';
import { JobsTab } from '../components/company/tabs/JobsTab';
import { ChatDock } from '../components/company/ChatDock';

// Lazy load LeadershipTab since it's a heavier component with org chart
const LeadershipTab = lazy(() => import('../components/company/tabs/LeadershipTab'));

type TabId = 'overview' | 'news' | 'products' | 'customers' | 'jobs' | 'leadership';

const tabs: { id: TabId; name: string }[] = [
  { id: 'overview', name: 'Overview' },
  { id: 'news', name: 'News' },
  { id: 'products', name: 'Products' },
  { id: 'customers', name: 'Customers' },
  { id: 'jobs', name: 'Jobs' },
  { id: 'leadership', name: 'Leadership' },
];

export function CompanyDetailPage() {
  const { companyId } = useParams<{ companyId: string }>();
  const { isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [isChatOpen, setIsChatOpen] = useState(false);

  const {
    data: overview,
    isLoading,
    error,
  } = useCompanyOverview(companyId || '', { enabled: isAuthenticated });

  if (isLoading) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="animate-pulse">
          <div className="h-10 w-64 bg-gray-200 rounded mb-4"></div>
          <div className="h-6 w-96 bg-gray-200 rounded mb-8"></div>
          <div className="h-96 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
    );
  }

  if (error || !overview) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="rounded-md bg-red-50 p-4">
          <h3 className="text-sm font-medium text-red-800">Error loading company</h3>
          <div className="mt-2 text-sm text-red-700">
            {error instanceof Error ? error.message : 'Company not found'}
          </div>
          <Link
            to="/companies"
            className="mt-4 inline-block text-sm text-red-600 hover:text-red-500"
          >
            ← Back to companies
          </Link>
        </div>
      </div>
    );
  }

  const { company, kb_status } = overview;

  return (
    <div className="flex">
      {/* Main content */}
      <div className={`flex-1 transition-all duration-300 ${isChatOpen ? 'mr-80' : ''}`}>
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="mb-4">
            <Link to="/companies" className="text-sm text-gray-500 hover:text-gray-700">
              Companies
            </Link>
            <span className="mx-2 text-gray-400">/</span>
            <span className="text-sm text-gray-900">{company.name}</span>
          </nav>

          {/* Company header */}
          <div className="mb-8">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{company.name}</h1>
                <div className="mt-2 flex items-center space-x-3">
                  {company.ticker && (
                    <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-sm font-medium text-blue-700">
                      {company.ticker}
                    </span>
                  )}
                  <span className="text-sm text-gray-500">
                    {company.company_type === 'public'
                      ? 'Public Company'
                      : company.company_type === 'pe_backed'
                        ? 'PE-Backed'
                        : 'Private'}
                  </span>
                  {company.headquarters && (
                    <>
                      <span className="text-gray-300">•</span>
                      <span className="text-sm text-gray-500">{company.headquarters}</span>
                    </>
                  )}
                </div>
              </div>
              <KBStatusBadge
                completeness={kb_status?.overall_completeness}
                isBuilding={kb_status?.is_building}
              />
            </div>
            {company.description && (
              <p className="mt-4 text-gray-600 max-w-3xl">{company.description}</p>
            )}
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium
                    ${
                      activeTab === tab.id
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                    }
                  `}
                >
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab content */}
          <div className="mt-6">
            {activeTab === 'overview' && <OverviewTab companyId={companyId!} overview={overview} />}
            {activeTab === 'news' && <NewsTab companyId={companyId!} />}
            {activeTab === 'products' && (
              <ProductsTab
                companyId={companyId!}
                synthesisProducts={overview?.synthesis?.products}
              />
            )}
            {activeTab === 'customers' && <CustomersTab companyId={companyId!} />}
            {activeTab === 'jobs' && <JobsTab companyId={companyId!} />}
            {activeTab === 'leadership' && (
              <Suspense
                fallback={
                  <div className="animate-pulse space-y-4">
                    <div className="h-8 w-48 bg-gray-200 rounded"></div>
                    <div className="h-64 bg-gray-200 rounded-lg"></div>
                  </div>
                }
              >
                <LeadershipTab
                  companyId={companyId!}
                  kbStatus={
                    kb_status
                      ? {
                          overall_completeness: kb_status.overall_completeness,
                          is_building: kb_status.is_building,
                        }
                      : undefined
                  }
                />
              </Suspense>
            )}
          </div>
        </div>
      </div>

      {/* Chat toggle button - moves left when chat is open to avoid overlapping input */}
      <button
        onClick={() => setIsChatOpen(!isChatOpen)}
        className={`fixed bottom-6 z-40 rounded-full bg-primary-600 p-4 text-white shadow-lg hover:bg-primary-700 transition-all duration-300 ${
          isChatOpen ? 'right-[21rem]' : 'right-6'
        }`}
        title={isChatOpen ? 'Close chat' : 'Open chat'}
      >
        {isChatOpen ? (
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        ) : (
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
            />
          </svg>
        )}
      </button>

      {/* Chat dock */}
      <ChatDock
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        companyId={companyId!}
        companyName={company.name}
        currentTab={activeTab}
      />
    </div>
  );
}

export default CompanyDetailPage;
